import React, { type FC, memo } from 'react';
import { Box, Button, Modal } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { Creative } from 'packages/store/creatives/types';
import DesignsList from './DesignsList';
import Preset from './Preset';
import Formats from './Formats';
import generateTestId from '~/helpers/testIdHelpers';
import { Alert } from 'packages/pages/designsBulkExportModal/Alert';
import useDesignsBulkExportModal from './useDesignsBulkExportModal';

type DesignsBulkExportModalProps = {
    isOpen: boolean;
    designs: Creative[];
    onClose: () => void;
};

const DesignsBulkExportModal: FC<DesignsBulkExportModalProps> = ({ isOpen, designs, onClose }) => {
    const { translate } = useTranslate();
    const {
        handleExport,
        isExporting,
        isLoadingRenditions,
        isDownloadBlocked,
        isWaitingRoomBlocked,
        exportableDesignsLength,
        exportResults,
        creativeTypes,
        alertMessage,
        handleSelectPreset,
        handleRenditionsLoad,
        handleSelectProfile,
    } = useDesignsBulkExportModal(designs, isOpen, onClose);

    return (
        <Modal
            title={translate('modal.bulk-export.title')}
            isOpen={isOpen}
            onClose={onClose}
            actionPrimary={
                <Button
                    variant="primary"
                    onClick={handleExport}
                    isLoading={isExporting}
                    isDisabled={
                        exportableDesignsLength === 0 ||
                        isLoadingRenditions ||
                        (isDownloadBlocked && isWaitingRoomBlocked)
                    }
                >
                    {translate('modal.bulk-export.export', { count: exportableDesignsLength })}
                </Button>
            }
            actionSecondary={
                <Button variant="secondary" onClick={onClose}>
                    {translate('modal.bulk-export.cancel')}
                </Button>
            }
            {...generateTestId('designs-bulk-export-modal')}
        >
            {!!alertMessage && <Alert>{alertMessage}</Alert>}
            <DesignsList designs={designs} exportResults={exportResults} />
            <Box paddingTop="5" paddingBottom="5">
                <Preset
                    isLoadingRenditions={isLoadingRenditions}
                    isDownloadBlocked={isDownloadBlocked}
                    isWaitingRoomBlocked={isWaitingRoomBlocked}
                    isExporting={isExporting}
                    onSelect={handleSelectPreset}
                />
                <Formats
                    creativeTypes={creativeTypes}
                    onLoadRenditions={handleRenditionsLoad}
                    onSelectProfile={handleSelectProfile}
                />
            </Box>
        </Modal>
    );
};

export default memo(DesignsBulkExportModal);
